<template>
  <div class="flex flex-1">
    <div class="space-y-3 flex-1 min-w-0 max-w-100 overflow-scroll">
      <uiList
        :items="offers"
        :search="['kind', 'about', 'successTitle', 'successText']"
        :add="true"
        width="auto"
        @add="setNewForm"
      >
        <template #items="{ items: offers }">
          <div class="space-y-3 flex-1 min-w-0 px-5 pt-2 pb-6 overflow-scroll">
            <div
              v-for="offer in offers"
              :key="offer.id"
              class="cursor-pointer space-y-1 rounded-2xl p-3"
              :class="{
                'bg-light-500': selected?.id !== offer.id,
                'bg-white shadow-lg': selected?.id === offer.id
              }"
              @click="handleSelectOffer(offer)"
            >
              <div class="flex items-center">
                <div class="flex space-x-1.5 items-center">
                  <p class="font-semibold m-0 text-base whitespace-nowrap">
                    {{ offer.kind }}
                  </p>
                </div>

                <p class="font-medium ml-auto m-0 text-xs text-zinc-500">
                  {{ offer.price }} {{ offer.currency }}
                </p>
              </div>

              <p class="font-medium m-0 text-sm opacity-50">
                {{ countryMap[offer.country] ?? offer.country }}
              </p>

              <p class="font-medium m-0 text-sm opacity-50">
                На {{ offer.period }}
                {{ pluralize(offer.period, ...intervalMap[offer.interval]) }}
              </p>
            </div>
          </div>
        </template>
      </uiList>
    </div>

    <div class="flex-1 min-w-0 max-w-110 p-8 overflow-scroll">
      <div v-if="selected != null" class="space-y-3">
        <div class="flex space-x-3">
          <uiInput v-model="selected.kind" label="Тип" class="flex-1" />

          <uiDropdown
            v-model="selected.country"
            label="Страна"
            :options="countryList"
            :reduce="item => item.value"
            clearSearchOnSelect
            class="flex-1"
          />
        </div>

        <div class="flex space-x-3">
          <uiInput
            v-model="selected.period"
            label="Период"
            mask="9999"
            class="flex-1"
          />

          <uiDropdown
            v-model="selected.interval"
            label=" "
            :options="intervalList"
            :reduce="item => item.value"
            clearSearchOnSelect
            class="flex-1"
          />
        </div>

        <div class="flex space-x-3">
          <uiInput
            v-model="selected.price"
            label="Стоимость"
            mask="9999"
            class="flex-1"
          />

          <uiInput
            v-model="selected.oldPrice"
            label="Старая цена"
            mask="9999"
            class="flex-1"
          />

          <uiDropdown
            v-model="selected.currency"
            label=" "
            :options="currencyList"
            :reduce="item => item.value"
            clearSearchOnSelect
            class="flex-1"
          />
        </div>

        <uiDropdown
          v-model="selected.provider"
          label="Провайдер"
          :options="providerList"
          :reduce="item => item.value"
          clearSearchOnSelect
          class="flex-1"
        />

        <uiTextarea v-model="selected.about" label="Описание" />

        <uiTextarea
          v-model="selected.successTitle"
          label="Заголовок после подписки"
        />

        <uiTextarea
          v-model="selected.successText"
          label="Текст после подписки"
        />

        <div class="flex space-x-3">
          <div>
            <p class="m-0 text-sm mb-1 opacity-50">Триал</p>
            <div class="py-2.5">
              <uiSwitch v-model="selected.trial" />
            </div>
          </div>

          <uiInput
            v-model="selected.trialPeriod"
            label=" "
            mask="999"
            placeholder="На сколько дней?"
          />
        </div>

        <uiTextarea v-model="selected.trialTitle" label="Заголовок триала" />

        <uiTextarea v-model="selected.trialText" label="Текст триала" />

        <div class="flex pt-3 justify-between">
          <div>
            <uiButton
              v-if="selected?.id"
              class="px-5"
              color="red"
              :loading="remove.isPending"
              @click="remove()"
            >
              Удалить
            </uiButton>
          </div>

          <uiButton
            class="px-5"
            :loading="save.isPending"
            :disabled="!canSave"
            @click="save()"
          >
            {{ selected?.id ? 'Сохранить' : 'Создать' }}
          </uiButton>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue'
import { format } from 'date-fns'
import { pluralize } from 'numeralize-ru'
import DatePicker from 'vue2-datepicker'
import api from '@/utils/ky'
import { API } from '@/config'
import { useAsyncMethod } from '@/utils/async-methods'
import { confirm, notify } from '@/utils/events'

const intervalMap = {
  day: ['день', 'дня', 'дней'],
  week: ['неделя', 'недели', 'недель'],
  month: ['месяц', 'месяца', 'месяцев'],
  year: ['год', 'года', 'лет']
}

const countryMap = {
  ru: 'Россия',
  il: 'Израиль',
  gb: 'Великобритания'
}

const currencyMap = {
  RUB: 'рублей',
  ILS: 'шекелей',
  EUR: 'евро'
}

export default {
  components: {
    DatePicker
  },
  setup () {
    const offers = ref([])
    const countryList = ref([])
    const currencyList = ref([])
    const intervalList = ref([])
    const providerList = ref([])

    async function setInitData ({
      offers: newOffers,
      countries,
      currencies,
      intervals,
      providers
    }) {
      offers.value = newOffers

      countryList.value = countries.reverse().map(value => ({
        label: countryMap[value] ?? value,
        value
      }))

      currencyList.value = currencies.map(value => ({
        label: currencyMap[value] ?? value,
        value
      }))

      intervalList.value = intervals.map(value => ({
        label: intervalMap[value]?.at(0) ?? value,
        value
      }))

      providerList.value = providers
    }

    onMounted(async () => {
      const { data } = await api.get(API.OFFERS.list)
      setInitData(data)
    })

    const selected = ref(null)
    function handleSelectOffer (offer) {
      selected.value = structuredClone(offer)
    }

    function setNewForm () {
      selected.value = {
        kind: '',
        country: countryList.value[0].value,
        price: undefined,
        oldPrice: undefined,
        currency: currencyList.value[0].value,
        provider: providerList.value[0],
        interval: intervalList.value[0].value,
        period: undefined,
        trial: false,
        trialPeriod: undefined,
        trialTitle: '',
        trialText: '',
        about: '',
        successTitle: '',
        successText: ''
      }
    }

    const canSave = computed(() => {
      return [
        selected.value.kind,
        selected.value.period,
        selected.value.interval,
        selected.value.price
      ].every(Boolean)
    })

    const save = useAsyncMethod(async () => {
      if (!canSave.value) return

      const payload = {
        ...selected.value,
        price: Number(selected.value.price),
        oldPrice: Number(selected.value.oldPrice) || undefined,
        period: Number(selected.value.period),
        trialPeriod: Number(selected.value.trialPeriod) || undefined
      }

      const { data } = selected.value?.id
        ? await api.post(API.OFFERS.update, payload)
        : await api.post(API.OFFERS.create, payload)

      if (data.err) return notify(data.err)

      setInitData(data)
      selected.value = null
    })

    const remove = useAsyncMethod(async () => {
      const confirmed = await confirm('Точно удаляем?')
      if (!confirmed) return

      const { data } = await api.post(API.OFFERS.delete, {
        id: selected.value?.id
      })

      setInitData(data)
      selected.value = null
    })

    return {
      pluralize,
      intervalMap,
      countryMap,
      offers,
      countryList,
      currencyList,
      intervalList,
      providerList,
      selected,
      handleSelectOffer,
      format,
      setNewForm,
      canSave,
      save,
      remove
    }
  }
}
</script>
